/* eslint-disable react-hooks/purity, react-hooks/immutability -- the compiler lint sees the dig handlers (clock, in-memory record state) as render-time calls; they only run in taps and effects */
import { ExternalLink, Heart, Pause, Play, Plus, Repeat, Shuffle, SkipBack, SkipForward } from 'lucide-react';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useAppStore, useStyleId } from '../../app/store';
import { styleEntry } from '../../data/styles';
import { STYLE_IDS, type StyleId } from '../../data/types';
import { createMark, getMark, softDeleteMark, updateMark, type NewMark } from '../../db/repos/marks';
import { listChannels, removeChannel, saveChannel } from '../../db/repos/channels';
import { listCrates, recordBad, recordHeard, setFavourite, setNotRecord } from '../../db/repos/dig';
import { buildPool, crateExpired, findCandidate, jumpPoint, pickTwoStage, stepWeight, whyLine, type Candidate, type CrateChannel, type CrateShard } from '../../lib/dig';
import { NOT_RECORD_REASONS, type NotRecordReason } from '../../lib/notRecord';
import { markMeta } from '../../lib/markMeta';
import { canNext, canPrev, current, drop, next, prev, push, type Session } from '../../lib/session';
import { addTap, bpmFromTaps, double, halve } from '../../lib/tempo';
import { formatTime } from '../../lib/time';
import { youtubeLink } from '../../lib/youtube';
import { BottomSheet } from '../../ui/BottomSheet';
import { Button, IconButton } from '../../ui/Button';
import { Readout } from '../../ui/Readout';
import { Row, RowList } from '../../ui/Row';
import { EmptyState, WhyLine } from '../../ui/States';
import { recordActivity } from '../beat/journalStore';
import { useActiveProject } from '../beat/projectsStore';
import screen from '../screen.module.css';
import { ChopGrid } from './ChopGrid';
import { MarkEditor } from './MarkEditor';
import { ChannelFeedback } from './ChannelFeedback';
import { DigSourceSheet } from './DigSourceSheet';
import { digSession, loadRecordState, noteDugChannel, recentChannels, recordState, type DigItem } from './recordState';
import { afterFirstPaint } from '../../lib/afterFirstPaint';
import { guarded } from '../../lib/writeGuard';
import { isShard, useCrate } from './useCrate';
import { useGenreCrate } from './useGenreCrate';
import { SegmentedControl } from '../../ui/SegmentedControl';
import { useYouTubePlayer } from './useYouTubePlayer';
import styles from './Dig.module.css';

const isStyleId = (s: string | null): s is StyleId => s !== null && (STYLE_IDS as readonly string[]).includes(s);
const rateLabel = (r: number) => `${r}×`;
const NO_GENRES: string[] = [];

/** Dig (SPEC F6, DESIGN_SYSTEM §4): press Dig, and a style-relevant record plays. */
const FirstRunDoors = lazy(() => import('./FirstRunDoors'));

export function DigScreen() {
  const styleId = useStyleId();
  const setStyle = useAppStore((s) => s.setStyle);
  const rediscover = useAppStore((s) => s.settings.rediscover);
  const showToast = useAppStore((s) => s.showToast);
  const project = useActiveProject();
  const [params, setSearchParams] = useSearchParams();
  const entry = styleEntry(styleId);
  const [crateTry, setCrateTry] = useState(0);
  const [painted, setPainted] = useState(false); // first-run doors load after the first paint
  useEffect(() => afterFirstPaint(() => setPainted(true)), []);
  const settings = useAppStore((s) => s.settings);
  const setDigPrefs = useAppStore((s) => s.setDigPrefs);
  // Source: the style, or genres picked by hand (the style has no effect then). Remembered locally.
  const genreMode = settings.digSource === 'genres';
  const digGenres = settings.digGenres ?? NO_GENRES;
  const includeNew = settings.includeNew ?? false;
  const pinned = settings.pinnedChannel;
  const styleCrate = useCrate(styleId, crateTry, genreMode);
  const genreCrate = useGenreCrate(digGenres, genreMode, crateTry);
  const [edits, setEdits] = useState<Record<string, Partial<CrateChannel>>>({});
  const rawChannels = genreMode ? genreCrate.channels : styleCrate.channels;
  const channels = useMemo(() => rawChannels.map((c) => (edits[c.id] ? { ...c, ...edits[c.id] } : c)), [rawChannels, edits]);
  const crate = {
    status: genreMode ? (genreCrate.status === 'ready' && digGenres.length === 0 ? 'empty' as const : genreCrate.status) : styleCrate.status,
    shard: styleCrate.shard,
    channels,
    noCrateAtAll: genreMode ? genreCrate.noCrateAtAll : styleCrate.noCrateAtAll,
  };
  const [sourceSheet, setSourceSheet] = useState(false);
  const [notRecordSheet, setNotRecordSheet] = useState(false);

  // /dig?style=tribe sets the style (SPEC §4 deep links)
  const urlStyle = params.get('style');
  useEffect(() => {
    if (isStyleId(urlStyle) && urlStyle !== useAppStore.getState().settings.styleId) setStyle(urlStyle);
  }, [urlStyle, setStyle]);

  // Heard, bad and favourite state lives in memory (recordState), so Dig can run inside the tap
  const [statesReady, setStatesReady] = useState(false);
  useEffect(() => {
    void loadRecordState().then(() => setStatesReady(true), () => setStatesReady(true));
  }, []);

  const [session, setSessionState] = useState<Session<DigItem>>(() => digSession.current);
  const setSession = (s: Session<DigItem>) => {
    digSession.current = s;
    setSessionState(s);
  };
  const record = current(session);
  const [unplayable, setUnplayable] = useState(false);
  const [inS, setIn] = useState<number | undefined>();
  const [outS, setOut] = useState<number | undefined>();
  const [looping, setLooping] = useState(false);
  const [taps, setTaps] = useState<number[]>([]);
  const [bpm, setBpm] = useState<number | undefined>();
  const [, refreshFavourite] = useState(0); // the heart reads recordState, so it only needs a nudge
  const favourite = record ? recordState.favourites.has(record.videoId) : false;
  const [marking, setMarking] = useState<{ inS: number; outS: number } | undefined>();
  const [speedSheet, setSpeedSheet] = useState(false);

  const { containerRef, view, act } = useYouTubePlayer({ loop: { on: looping, inS, outS }, onSkipError: () => onBad() });
  // A record on screen that this player hasn't started yet (reopened, or returned to) waits for a tap: the app's Play, or YouTube's own
  const armed = record !== undefined && view.startedId !== record.videoId;
  // Show it in the player meanwhile (cueing needs no tap), so YouTube's own play button has something to play
  const cueId = armed && !unplayable ? record.videoId : undefined;
  const cueAt = record?.mark?.inS;
  const cue = act.cue;
  useEffect(() => {
    if (cueId && view.status !== 'loading' && view.status !== 'offline') cue(cueId, cueAt);
  }, [cueId, cueAt, cue, view.status]);

  const resetFor = (it: DigItem) => {
    setIn(it.mark?.inS);
    setOut(it.mark?.outS);
    setLooping(Boolean(it.mark));
    setTaps([]);
    setBpm(it.mark?.bpm);
    setMarking(undefined);
    setUnplayable(false);
  };

  const pick = (): DigItem | undefined => {
    const base = { heardAt: recordState.heardAt, bad: recordState.bad, notRecord: recordState.notRecord, rediscover, now: Date.now(), includeNew };
    let pool: Candidate[];
    if (genreMode) {
      // Genres mode: the style has no effect. A record in two picked genres counts once.
      const seen = new Set<string>();
      pool = genreCrate.shards.flatMap((s) => buildPool(s.shard, channels, undefined, { ...base, genre: s.genre })).filter((c) => !seen.has(c.videoId) && Boolean(seen.add(c.videoId)));
    } else {
      if (!crate.shard || !entry) return undefined;
      pool = buildPool(crate.shard, channels, entry, base);
    }
    // A pinned channel that has run out of records is unpinned
    let stay = pinned;
    if (stay && !pool.some((c) => c.channelId === stay)) {
      setDigPrefs({ pinnedChannel: undefined });
      showToast({ message: `${channels.find((c) => c.id === stay)?.name ?? 'That channel'} has no more records to dig. Unpinned.` });
      stay = undefined;
    }
    const c = pickTwoStage(pool, Math.random, recentChannels.ids, stay);
    if (c) noteDugChannel(c.channelId);
    return c && { videoId: c.videoId, candidate: c, reopened: false };
  };

  /** Must run inside a tap: it calls loadVideoById (the iPhone needs the first play to come from a tap). */
  const show = (it: DigItem, s: Session<DigItem>) => {
    setSession(s);
    resetFor(it);
    act.load(it.videoId, it.mark?.inS ?? 0);
  };

  // A mark or a history row was tapped (/dig?mark=ID or /dig?record=ID): stage it, then Play loads it inside its own tap.
  // This is not a dig: nothing is written, so the 90-day rule and the weights don't move. It only joins the session.
  const markParam = params.get('mark');
  const recordParam = params.get('record');
  const arrived = useRef<string | undefined>(undefined);
  useEffect(() => {
    const key = markParam ? `m:${markParam}` : recordParam ? `r:${recordParam}` : undefined;
    if (!key || arrived.current === key || crate.status === 'loading' || !statesReady) return;
    arrived.current = key;
    void (async () => {
      const mark = markParam ? await getMark(markParam).catch(() => undefined) : undefined;
      const videoId = mark?.videoId ?? recordParam ?? undefined;
      setSearchParams({}, { replace: true });
      if (!videoId) {
        showToast({ message: 'That mark is not there any more' });
        return;
      }
      const stored = await listCrates().catch(() => []);
      const current = genreMode ? genreCrate.shards.map((s) => s.shard) : [crate.shard];
      const shards = [...current, ...stored.filter((c) => c.styleId !== styleId).map((c) => c.shard)].filter(
        (x): x is CrateShard => isShard(x) && !crateExpired(x, Date.now()),
      );
      const candidate = findCandidate(shards, crate.channels, entry?.crateKeywords ?? [], videoId);
      const it: DigItem = { videoId, candidate, mark, reopened: true };
      setSession(push(digSession.current, it));
      resetFor(it);
    })();
  });

  const noteHeard = (it: DigItem) => {
    const now = Date.now();
    recordState.heardAt[it.videoId] = now;
    void recordHeard(it.videoId, styleId, now, 'played');
  };

  const dig = () => {
    const c = pick();
    if (!c) {
      showToast({ message: genreMode ? 'Nothing left to dig for these genres. Turn on Rediscover in History, or pick more genres.' : 'Nothing left to dig for this style. Turn on Rediscover in History, or add channels.' });
      return;
    }
    show(c, push(session, c));
    noteHeard(c);
  };

  const goPrev = () => {
    if (!canPrev(session)) return;
    const s = prev(session);
    const c = current(s);
    if (c) show(c, s);
  };
  const goNext = () => {
    if (!canNext(session)) return dig();
    const s = next(session);
    const c = current(s);
    if (c) show(c, s);
  };

  // Errors 100, 101, 150: mark the record bad and dig the next one (SPEC F6)
  function onBad() {
    const c = record;
    if (!c) return;
    // A reopened record was asked for by name: say so, never skip it or mark it bad
    if (c.reopened) {
      setUnplayable(true);
      return;
    }
    recordState.bad.add(c.videoId);
    void recordBad(c.videoId, styleId, Date.now());
    const s = drop(session, (i) => i.videoId === c.videoId);
    const n = pick();
    showToast({ message: "Skipped a record that can't be played here" });
    if (n) {
      show(n, push(s, n));
      noteHeard(n);
    } else setSession(s);
  }

  const togglePlay = () => {
    if (!record) return dig();
    if (armed || view.errorCode !== undefined) {
      // A reopened (or returned-to) record, or one YouTube refused: this tap loads it, so loadVideoById runs right here
      act.load(record.videoId, record.mark?.inS ?? 0);
      return;
    }
    act.togglePlay();
  };
  const jump = () => {
    if (!record || armed) return;
    const d = view.durationS || 0;
    if (d > 0) act.seekTo(jumpPoint(d, Math.random));
  };
  const markIn = () => {
    if (record && !armed) setIn(act.getTime());
  };
  const markOut = () => {
    if (record && !armed) setOut(act.getTime());
  };
  const toggleLoop = () => {
    if (inS !== undefined && outS !== undefined && outS > inS) {
      setLooping((l) => !l);
      if (!looping) act.seekTo(inS);
    }
  };
  const tap = () => {
    if (!record) return;
    const t = addTap(taps, performance.now());
    setTaps(t);
    const b = bpmFromTaps(t);
    if (b) setBpm(b);
  };
  const toggleFavourite = () => {
    if (!record) return;
    const v = !favourite;
    refreshFavourite((n) => n + 1);
    if (v) recordState.favourites.add(record.videoId);
    else recordState.favourites.delete(record.videoId);
    void setFavourite(record.videoId, v);
  };
  const cand0 = record?.candidate;
  const channelNow = cand0 ? channels.find((c) => c.id === cand0.channelId) : undefined;
  /** More and Less on the Why line: step the channel's local weight (off, 1, 2, 3, 4). Local edits win over the shipped registry. */
  const feedback = (dir: 'more' | 'less') =>
    guarded(async () => {
      const ch = channelNow;
      if (!ch) return;
      const next = stepWeight(ch, dir);
      if (next.weight === ch.weight && next.enabled === ch.enabled) {
        showToast({ message: dir === 'more' ? `${ch.name} is already at weight 4` : `${ch.name} is already off` });
        return;
      }
      const before = { weight: ch.weight, enabled: ch.enabled };
      const local = (await listChannels()).find((l) => l.id === ch.id && !l.deleted);
      const savedId = await saveChannel({ ...(local ?? { name: ch.name, id: ch.id, styles: ch.styles as StyleId[], genres: ch.genres, era: ch.era }), ...next });
      setEdits((e) => ({ ...e, [ch.id]: next }));
      showToast({
        message: !next.enabled ? `${ch.name} is off` : `${dir === 'more' ? 'More' : 'Less'} from ${ch.name}, weight ${next.weight}`,
        action: {
          label: 'Undo',
          run: () =>
            void guarded(async () => {
              if (local) await saveChannel({ ...local, ...before });
              else await removeChannel(savedId);
              setEdits((e) => ({ ...e, [ch.id]: before }));
            }),
        },
      });
    });
  /** Stay on this channel: the next digs come from it until unpinned. */
  const togglePin = () => {
    if (!cand0) return;
    const on = pinned !== cand0.channelId;
    setDigPrefs({ pinnedChannel: on ? cand0.channelId : undefined });
    showToast({ message: on ? `Staying on ${cand0.channelName} until you unpin it` : 'Unpinned' });
  };
  /** Not a record: never dug again (like Bad), with the reason. 2 taps: the button, then the reason. Runs inside the tap (it loads the next record). */
  const markNotRecord = (reason: NotRecordReason) => {
    const c = record;
    setNotRecordSheet(false);
    if (!c) return;
    recordState.notRecord.set(c.videoId, reason);
    void guarded(() => setNotRecord(c.videoId, reason, Date.now()));
    const s = drop(session, (i) => i.videoId === c.videoId);
    const n = pick();
    showToast({
      message: 'Marked not a record',
      action: {
        label: 'Undo',
        run: () => {
          recordState.notRecord.delete(c.videoId);
          void guarded(() => setNotRecord(c.videoId, undefined, Date.now()));
        },
      },
    });
    if (n) {
      show(n, push(s, n));
      noteHeard(n);
    } else setSession(s);
  };
  const chooseSource = (v: 'style' | 'genres') => {
    setDigPrefs({ digSource: v });
    if (v === 'genres' && digGenres.length === 0) setSourceSheet(true);
  };
  const toggleGenre = (g: string) => setDigPrefs({ digGenres: digGenres.includes(g) ? digGenres.filter((x) => x !== g) : [...digGenres, g] });
  /** A Chops pad was tapped: seek there and play. A record the player has not started yet is loaded at the cue (inside this tap). */
  const playCue = (atS: number) => {
    if (!record || !playReady) return;
    if (armed || view.errorCode !== undefined) {
      act.load(record.videoId, atS);
      return;
    }
    act.seekTo(atS);
    if (view.status !== 'playing') act.togglePlay();
  };

  const openOnYouTube = () => {
    if (record) window.open(youtubeLink(record.videoId, armed ? inS ?? 0 : act.getTime()), '_blank', 'noopener');
  };

  const saveMark = async (input: NewMark) => {
    const saved = record?.mark;
    if (saved) {
      // Editing a reopened mark: update it in place, with Undo back to what it was
      const patch = { inS: input.inS, outS: input.outS, bpm: input.bpm, keyRoot: input.keyRoot, minor: input.minor, kind: input.kind, note: input.note, projectId: input.projectId };
      const before = { inS: saved.inS, outS: saved.outS, bpm: saved.bpm, keyRoot: saved.keyRoot, minor: saved.minor, kind: saved.kind, note: saved.note, projectId: saved.projectId };
      await updateMark(saved.id, patch);
      if (patch.projectId) recordActivity(patch.projectId, { phaseId: 'dig' });
      const updated = { ...saved, ...patch };
      setSession({ ...session, items: session.items.map((x, i) => (i === session.cursor ? { ...x, mark: updated } : x)) });
      setIn(updated.inS);
      setOut(updated.outS);
      setMarking(undefined);
      showToast({ message: 'Mark updated', action: { label: 'Undo', run: () => void updateMark(saved.id, before) } });
      return;
    }
    const m = await createMark(input, styleId, Date.now());
    if (m.projectId) recordActivity(m.projectId, { phaseId: 'dig' });
    setMarking(undefined);
    showToast({ message: 'Mark saved', action: { label: 'Undo', run: () => void softDeleteMark(m.id, Date.now()) } });
  };

  // Keys (SPEC F6): D dig, F jump, Z in, C out, X loop, B tap BPM, W favourite
  const keys = useRef({ dig, jump, markIn, markOut, toggleLoop, tap, toggleFavourite });
  useEffect(() => {
    keys.current = { dig, jump, markIn, markOut, toggleLoop, tap, toggleFavourite };
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.ctrlKey || e.metaKey || e.altKey || t?.closest('input, textarea, select, [role="dialog"]')) return;
      const k = e.key.toLowerCase();
      const map: Record<string, () => void> = {
        d: () => keys.current.dig(), f: () => keys.current.jump(), z: () => keys.current.markIn(), c: () => keys.current.markOut(),
        x: () => keys.current.toggleLoop(), b: () => keys.current.tap(), w: () => keys.current.toggleFavourite(),
      };
      const run = map[k];
      if (run) {
        e.preventDefault();
        run();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // The real player has no loadVideoById until its onReady has fired, so nothing plays before that
  const playerReady = view.status !== 'loading' && view.status !== 'offline';
  const ready = crate.status === 'ready' && statesReady && playerReady;
  // Play also works for a reopened record when this style has no crate: it plays by video ID
  const playReady = statesReady && playerReady && (Boolean(record) || crate.status === 'ready');
  const cand = record?.candidate;
  const why = cand ? whyLine(cand, crate.channels.find((c) => c.id === cand.channelId)) : undefined;
  const heading = !record ? 'Dig' : cand ? cand.title : record.mark ? markMeta(record.mark) : 'Record from your history';
  const loopReady = inS !== undefined && outS !== undefined && outS > inS;
  const playing = view.status === 'playing';
  const crateExpiredNow = crate.status === 'expired';
  const placeholder = view.status === 'offline' || crateExpiredNow ? 'Needs connection' : view.status === 'loading' ? 'Loading the player' : undefined;

  return (
    <div className={screen.screen}>
      <div className={styles.source}>
        <SegmentedControl<'style' | 'genres'>
          label="Dig source"
          value={genreMode ? 'genres' : 'style'}
          onChange={chooseSource}
          segments={[{ value: 'style', label: 'Style' }, { value: 'genres', label: 'Genres' }]}
        />
        <Button variant="tertiary" aria-label="Dig source options" onClick={() => setSourceSheet(true)}>{genreMode ? (digGenres.length > 0 ? digGenres.join(', ') : 'Pick genres') : 'Options'}</Button>
      </div>
      <div className={styles.player}>
        <div ref={containerRef} className={styles.host} />
        {placeholder && <p className={styles.placeholder} role="status">{placeholder}</p>}
      </div>
      {(view.status === 'offline' || crateExpiredNow) && (
        <Button variant="tertiary" onClick={view.status === 'offline' ? act.retry : () => setCrateTry((n) => n + 1)}>Try again</Button>
      )}

      <h1 className={`t-section ${styles.title}`}>{heading}</h1>
      {why ? (
        <>
          <div className={styles.why}>
            <WhyLine from={why.from} parts={why.parts} />
          </div>
          {channelNow && cand0 && (
            <ChannelFeedback
              channelName={cand0.channelName}
              pinned={pinned === cand0.channelId}
              onMore={() => void feedback('more')}
              onLess={() => void feedback('less')}
              onTogglePin={togglePin}
            />
          )}
        </>
      ) : record ? (
        <p className={`t-meta ${styles.helper}`}>{record.mark ? 'Saved mark, not in the current crate' : 'Not in the current crate'}</p>
      ) : (
        <p className={`t-meta ${styles.helper}`}>{crate.status === 'empty' ? '' : crateExpiredNow ? 'The crate is out of date. Connect to refresh it.' : genreMode ? `Press Dig for a record from ${digGenres.join(', ')}` : `Press Dig for a record that fits ${entry?.name ?? 'your style'}`}</p>
      )}
      {(view.status === 'blocked' || (armed && playerReady)) && <p className={`t-label ${styles.blocked}`} role="status">Tap to play</p>}
      {!unplayable && view.errorCode !== undefined && record && (
        <>
          <p className={`t-label ${styles.blocked}`} role="status">YouTube could not play this here (error {view.errorCode}). Tap Play to try again.</p>
          <a className={styles.link} href={youtubeLink(record.videoId, record.mark?.inS)} target="_blank" rel="noopener noreferrer">Open on YouTube</a>
        </>
      )}
      {unplayable && record && (
        <>
          <p className={`t-label ${styles.blocked}`} role="status">This video can no longer be played in the app</p>
          <a className={styles.link} href={youtubeLink(record.videoId, record.mark?.inS)} target="_blank" rel="noopener noreferrer">Open on YouTube</a>
        </>
      )}

      {crate.status === 'empty' && genreMode && !crate.noCrateAtAll && (
        <EmptyState
          message={digGenres.length === 0 ? 'Pick genres to dig by genre.' : 'No records for these genres yet.'}
          action={{ label: 'Pick genres', onClick: () => setSourceSheet(true) }}
        />
      )}
      {crate.status === 'empty' && !(genreMode && !crate.noCrateAtAll) && (
        <EmptyState
          message={
            crate.noCrateAtAll
              ? 'No crate yet. Build and deploy the crate, or import channels.json.'
              : `No records for ${entry?.name ?? 'this style'} yet. Add channels in Dig › Channels, then run the crate build.`
          }
          action={{ label: 'Open Channels', to: '/dig/channels' }}
        />
      )}

      {marking && record ? (
        <MarkEditor
          videoId={record.videoId}
          initial={record.mark}
          inS={marking.inS}
          outS={marking.outS}
          bpm={bpm}
          durationS={view.durationS}
          projectId={project?.id}
          projectName={project?.name}
          onSave={(m) => void guarded(() => saveMark(m))}
          onCancel={() => setMarking(undefined)}
        />
      ) : (
        <div className={styles.controls}>
          <hr className={styles.rule} />
          <div className={styles.transport}>
            <Button icon={SkipBack} disabled={!canPrev(session)} onClick={goPrev} className={styles.tight}>Prev</Button>
            <Button icon={playing ? Pause : Play} onClick={togglePlay} disabled={!playReady}>{playing ? 'Pause' : 'Play'}</Button>
            <Button icon={Shuffle} disabled={!record || armed} onClick={jump} className={styles.tight}>Jump</Button>
            <Button icon={SkipForward} iconEnd disabled={!ready} onClick={goNext} className={styles.tight}>Next</Button>
          </div>

          <div className={styles.loop}>
            <Button stack disabled={!record || armed} onClick={markIn} aria-label={`In, ${inS === undefined ? 'not set' : formatTime(inS)}`}>
              <span className={styles.small}>In</span>
              <span className={styles.value}>{inS === undefined ? '—' : formatTime(inS)}</span>
            </Button>
            <Button icon={Repeat} aria-pressed={looping} disabled={!loopReady} onClick={toggleLoop} className={styles.tight}>Loop</Button>
            <Button stack disabled={!record || armed} onClick={markOut} aria-label={`Out, ${outS === undefined ? 'not set' : formatTime(outS)}`}>
              <span className={styles.small}>Out</span>
              <span className={styles.value}>{outS === undefined ? '—' : formatTime(outS)}</span>
            </Button>
            <Readout label="BPM" value={bpm === undefined ? '—' : String(bpm)} onClick={record ? tap : undefined} disabled={!record} actionLabel="tap to set tempo" className={styles.readout} />
          </div>
          <p className={`t-meta ${styles.helper}`}>Preview loop, accurate to about 0.1 s</p>
          {bpm !== undefined && (
            <div className={styles.halfDouble}>
              <Button variant="tertiary" onClick={() => setBpm(halve(bpm))}>Half tempo, ½×</Button>
              <Button variant="tertiary" onClick={() => setBpm(double(bpm))}>Double tempo, 2×</Button>
            </div>
          )}

          <Button variant="dig" disabled={!ready} onClick={dig}>Dig</Button>

          <div className={styles.last}>
            <IconButton icon={Heart} aria-label="Favourite" aria-pressed={favourite} disabled={!record} onClick={toggleFavourite} />
            <Button icon={Plus} disabled={!record} onClick={() => setMarking({ inS: inS ?? 0, outS: outS ?? Math.max(inS ?? 0, act.getTime()) })}>Mark</Button>
            <Button aria-label={`Speed ${rateLabel(view.rate)}`} disabled={!record} onClick={() => setSpeedSheet(true)}>
              <span className={styles.label}>Speed</span>
              <span className={styles.value}>{rateLabel(view.rate)}</span>
            </Button>
            <IconButton icon={ExternalLink} aria-label="Open on YouTube" disabled={!record} onClick={openOnYouTube} />
          </div>
          <Button disabled={!cand} onClick={() => setNotRecordSheet(true)}>Not a record</Button>

          {record && !unplayable && <ChopGrid videoId={record.videoId} durationS={view.durationS} armed={armed} getTime={act.getTime} playAt={playCue} />}

          {/* Below the Dig button, so nothing above it moves when the rows arrive after the first paint */}
          {painted && (<Suspense fallback={null}><FirstRunDoors styleName={entry?.name ?? 'your style'} canDig={ready} onDig={dig} /></Suspense>)}

          <RowList label="Dig tools">
            <li><Row title="Marks" meta="Your saved loops" chevron to="/dig/marks" /></li>
            <li><Row title="History" meta="Records you have dug" chevron to="/dig/history" /></li>
            <li><Row title="Channels" meta="Which channels feed the crate" chevron to="/dig/channels" /></li>
            <li><Row title="Sampling guide" meta="Phone to MPC, step by step" chevron to="/dig/guide" /></li>
          </RowList>
        </div>
      )}

      <DigSourceSheet
        open={sourceSheet}
        onClose={() => setSourceSheet(false)}
        genreMode={genreMode}
        index={genreCrate.index}
        picked={digGenres}
        onToggleGenre={toggleGenre}
        includeNew={includeNew}
        onIncludeNew={(v) => setDigPrefs({ includeNew: v })}
        canDig={ready && (!genreMode || digGenres.length > 0)}
        onDig={() => {
          setSourceSheet(false);
          dig();
        }}
      />

      <BottomSheet open={notRecordSheet} title="Not a record" onClose={() => setNotRecordSheet(false)}>
        <div role="group" aria-label="Why is it not a record">
          {NOT_RECORD_REASONS.map((r) => (
            <button key={r.value} type="button" className={styles.rateRow} onClick={() => markNotRecord(r.value)}>{r.label}</button>
          ))}
        </div>
      </BottomSheet>

      <BottomSheet open={speedSheet} title="Speed" onClose={() => setSpeedSheet(false)}>
        <div role="radiogroup" aria-label="Speed">
          {view.rates.map((r) => (
            <button
              key={r}
              type="button"
              role="radio"
              aria-checked={r === view.rate}
              className={styles.rateRow}
              onClick={() => {
                act.setRate(r);
                setSpeedSheet(false);
              }}
            >
              {rateLabel(r)}
            </button>
          ))}
        </div>
      </BottomSheet>
    </div>
  );
}
